import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  ShieldAlert,
  AlertTriangle,
  RotateCcw,
  Layers,
  Flame,
  Loader2,
  Info,
} from "lucide-react";
import { trpcClient } from "@/lib/trpc";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface PlateDamageRemakeModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  plate: {
    id: string;
    plateId?: string;
    name?: string;
    productName?: string;
    customerName?: string;
    size?: string;
    colors?: any;
    colorNames?: string[];
    activeMachineId?: string;
    activeMachineName?: string;
    mountedBy?: string;
    remakeCount?: number;
  } | null;
  onSuccess?: (result?: any) => void;
}

const DAMAGE_REASONS = [
  { value: "SCRATCH", label: "Trầy xước bề mặt kẽm do cát / bụi giấy" },
  { value: "CRACK", label: "Nứt / gãy ngàm kẽm khi xiết kẽm" },
  { value: "COATING_PEEL", label: "Bong tróc lớp thuốc cảm quang kẽm" },
  { value: "EXPOSURE_ERROR", label: "Lỗi ghi tram / phơi kẽm / lỗi CTP" },
  { value: "OPERATOR_ERROR", label: "Lỗi thao tác lắp kẽm của thợ máy" },
  { value: "OTHER", label: "Lý do kỹ thuật khác" },
];

export default function PlateDamageRemakeModal({
  open,
  onOpenChange,
  plate,
  onSuccess,
}: PlateDamageRemakeModalProps) {
  const queryClient = useQueryClient();
  const [damagedColors, setDamagedColors] = useState<string[]>([]);
  const [reasonCode, setReasonCode] = useState("SCRATCH");
  const [customReason, setCustomReason] = useState("");
  const [damageNotes, setDamageNotes] = useState("");
  const [remakeQty, setRemakeQty] = useState(1);
  const [urgency, setUrgency] = useState<"NORMAL" | "URGENT">("URGENT");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const availableColors = React.useMemo(() => {
    if (!plate) return ["Cyan (C)", "Magenta (M)", "Yellow (Y)", "Black (K)"];
    if (Array.isArray(plate.colors) && plate.colors.length > 0) {
      return plate.colors;
    }
    if (Array.isArray(plate.colorNames) && plate.colorNames.length > 0) {
      return plate.colorNames;
    }
    return ["Cyan (C)", "Magenta (M)", "Yellow (Y)", "Black (K)"];
  }, [plate]);

  useEffect(() => {
    if (open && plate) {
      setDamagedColors(availableColors.length > 0 ? [availableColors[0]] : []);
      setReasonCode("SCRATCH");
      setCustomReason("");
      setDamageNotes("");
      setRemakeQty(1);
      setUrgency("URGENT");
    }
  }, [open, plate, availableColors]);

  const toggleColor = (color: string) => {
    setDamagedColors((prev) =>
      prev.includes(color) ? prev.filter((c) => c !== color) : [...prev, color]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!plate) return;

    if (damagedColors.length === 0) {
      toast.error("Vui lòng chọn ít nhất 1 màu kẽm bị hỏng cần ghi bù");
      return;
    }

    const selectedReasonObj = DAMAGE_REASONS.find((r) => r.value === reasonCode);
    const finalReason =
      reasonCode === "OTHER"
        ? customReason.trim() || "Lý do khác"
        : selectedReasonObj?.label || reasonCode;

    if (!finalReason) {
      toast.error("Vui lòng chọn hoặc nhập lý do hỏng kẽm");
      return;
    }

    setIsSubmitting(true);
    try {
      const result = await (trpcClient as any).technical.plates.reportDamagedAndRemake.mutate({
        id: plate.id,
        damagedColors,
        damageReason: finalReason,
        damageNotes: damageNotes.trim() || undefined,
        remakeQty: Number(remakeQty) || 1,
        urgency,
      });

      toast.success(
        `Đã kích hoạt Lệnh Ghi Bù Kẽm CTP (${urgency === "URGENT" ? "CẤP TỐC" : "Thường"}) thành công!`
      );

      await queryClient.invalidateQueries({ queryKey: ["plate", plate.id] });
      await queryClient.invalidateQueries({ queryKey: ["plates"] });
      await queryClient.invalidateQueries({ queryKey: ["plate-usage-history"] });

      onSuccess?.(result);
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Lỗi khi báo hỏng và ghi bù kẽm");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!plate) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className="p-2 bg-rose-50 text-rose-600 rounded-lg">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg font-bold text-rose-900">
                Báo Hỏng Bản Kẽm & Yêu Cầu Ghi Bù Cấp Tốc
              </DialogTitle>
              <DialogDescription className="text-xs text-rose-600">
                Kích hoạt lệnh ưu tiên số 1 cho Tổ Chế Bản CTP để giảm thời gian dừng máy in (Downtime)
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Banner cảnh báo khẩn cấp */}
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg flex items-start gap-2.5">
            <AlertTriangle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
            <div className="text-xs text-rose-800 space-y-0.5">
              <p className="font-bold">Cảnh báo sự cố máy in Offset:</p>
              <p>
                Máy in: <span className="font-semibold text-rose-900">{plate.activeMachineName || "Máy Offset xưởng"}</span> • Thợ máy: <span className="font-semibold text-rose-900">{plate.mountedBy || "Tổ In"}</span>.
                Kẽm ghi bù sẽ tự động ghi vết hao hụt vào Nhật ký CTP để kế toán phân bổ chi phí.
              </p>
            </div>
          </div>

          {/* Chọn lá màu kẽm hỏng */}
          <div className="space-y-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-semibold text-slate-800 flex items-center gap-1">
                <Layers className="w-3.5 h-3.5 text-indigo-500" />
                Màu Kẽm Bị Sự Cố Cần Ghi Bù <span className="text-rose-500">*</span>
              </Label>
              <span className="text-[11px] text-slate-500">
                (Đã chọn: {damagedColors.length} màu)
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 pt-1">
              {availableColors.map((colorName: string) => {
                const isChecked = damagedColors.includes(colorName);
                return (
                  <div
                    key={colorName}
                    onClick={() => toggleColor(colorName)}
                    className={`flex items-center gap-2 p-2 rounded border cursor-pointer select-none text-xs transition-colors ${
                      isChecked
                        ? "bg-rose-50 border-rose-300 text-rose-900 font-bold"
                        : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
                    }`}
                  >
                    <Checkbox
                      checked={isChecked}
                      onCheckedChange={() => toggleColor(colorName)}
                      className="pointer-events-none data-[state=checked]:bg-rose-600 data-[state=checked]:border-rose-600"
                    />
                    <span className="break-words whitespace-normal">{colorName}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Lý do hỏng & Độ khẩn cấp */}
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2 sm:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-700">
                Lý Do Sự Cố Hỏng Kẽm <span className="text-rose-500">*</span>
              </Label>
              <Select value={reasonCode} onValueChange={setReasonCode}>
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder="Chọn lý do..." />
                </SelectTrigger>
                <SelectContent>
                  {DAMAGE_REASONS.map((r) => (
                    <SelectItem key={r.value} value={r.value} className="text-xs">
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="col-span-2 sm:col-span-1 space-y-1">
              <Label className="text-xs font-semibold text-slate-700 flex items-center gap-1">
                <Flame className="w-3.5 h-3.5 text-amber-500" />
                Mức Độ Ưu Tiên Ghi Bù <span className="text-rose-500">*</span>
              </Label>
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => setUrgency("URGENT")}
                  className={`p-1.5 rounded border text-xs font-semibold transition-all ${
                    urgency === "URGENT"
                      ? "bg-rose-600 text-white border-rose-600 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  ⚡ Dừng Máy Chờ Kẽm
                </button>
                <button
                  type="button"
                  onClick={() => setUrgency("NORMAL")}
                  className={`p-1.5 rounded border text-xs font-semibold transition-all ${
                    urgency === "NORMAL"
                      ? "bg-slate-800 text-white border-slate-800 shadow-sm"
                      : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"
                  }`}
                >
                  ⏳ Bình Thường
                </button>
              </div>
            </div>

            {reasonCode === "OTHER" && (
              <div className="col-span-2 space-y-1">
                <Label className="text-xs font-medium text-slate-700">
                  Mô Tả Lý Do Khác <span className="text-rose-500">*</span>
                </Label>
                <Input
                  placeholder="Mô tả cụ thể sự cố kẽm..."
                  value={customReason}
                  onChange={(e) => setCustomReason(e.target.value)}
                  className="h-8 text-xs"
                  required
                />
              </div>
            )}

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700 flex items-center gap-1">
                <RotateCcw className="w-3.5 h-3.5 text-slate-400" />
                Số Lượng Lá Kẽm Ghi Bù (Lá)
              </Label>
              <Input
                type="number"
                min="1"
                max="20"
                value={remakeQty}
                onChange={(e) => setRemakeQty(Math.max(1, parseInt(e.target.value) || 1))}
                className="h-8 text-xs font-mono font-bold"
              />
            </div>

            <div className="space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                Đã Ghi Bù Trước Đây
              </Label>
              <div className="h-8 px-3 rounded border border-slate-200 bg-slate-100 flex items-center text-xs font-mono text-slate-700">
                {plate.remakeCount || 0} lần
              </div>
            </div>

            <div className="col-span-2 space-y-1">
              <Label className="text-xs font-medium text-slate-700">
                Chi Tiết Sự Cố & Yêu Cầu Căn Chỉnh Tổ CTP
              </Label>
              <Textarea
                rows={2}
                placeholder="VD: Lá M bị xước sọc ngang khổ in góc dưới bên trái, cần ghi bản lại gấp trước 15:30..."
                value={damageNotes}
                onChange={(e) => setDamageNotes(e.target.value)}
                className="text-xs resize-none"
              />
            </div>
          </div>

          <DialogFooter className="pt-2 border-t border-slate-100 flex items-center justify-between sm:justify-between">
            <div className="flex items-center gap-1.5 text-slate-400 text-xs">
              <Info className="w-3.5 h-3.5" />
              <span>Ghi nhận hao hụt vào CtpPlateLog & tăng remakeCount</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => onOpenChange(false)}
                disabled={isSubmitting}
              >
                Hủy
              </Button>
              <Button
                type="submit"
                size="sm"
                className="bg-rose-600 hover:bg-rose-700 text-white"
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                    Đang kích hoạt...
                  </>
                ) : (
                  <>
                    <RotateCcw className="w-3.5 h-3.5 mr-1.5" />
                    Kích Hoạt Lệnh Ghi Bù Kẽm
                  </>
                )}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
